// libraries
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import type { StateTree } from '@makemydeal/dr-dash-types';
import type { ReduxMiddleware, ReduxStore, ReduxNext } from '@makemydeal/dr-shared-store';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { AccelerateVehicleCondition, LEASE } from '@makemydeal/dr-platform-types';
import type { CalculationEndpointType, OfferType } from '@makemydeal/dr-platform-types';
import { actions as offerReduxActionTypes } from '@makemydeal/dr-offer-redux';

// actions
import { updateCurrentCalculationEndpoint, setProgramFallbackOverrides } from '../actions/offerRedux';

// action types
import { PAYMENT_RECEIVED } from '../actionTypes/deskingActionTypes';
import { APPLY_LEASE_CONTRACT_FIELDS } from '../actionTypes/leaseContractingFieldsActionTypes';

// constants / selectors
import { CALC_ENDPOINTS } from '../constants';
import * as offerReduxSelectors from '../selectors/offerRedux';

// shared
import { CALC_REQUEST_ID_META, markStalePaymentResponse, stampRequestId } from './__common__/calcRequestId';
import { isPaymentGridOriginated } from './__common__/paymentGridOrigin';
import { mileageOverrideNeedsUserProgramQuotes, overrideKeepsCalculationEndpoint } from './__common__/pqSupportedOverrides';

const UPQ_TRIGGER_ACTIONS: ReadonlySet<string> = new Set([
    offerReduxActionTypes.UPDATED_BASE_RESIDUAL_DOLLAR_OVERRIDE,
    offerReduxActionTypes.UPDATED_BASE_RESIDUAL_PERCENT_OVERRIDE,
    offerReduxActionTypes.UPDATED_PROGRAM_RESIDUAL_DOLLAR_OVERRIDE,
    offerReduxActionTypes.UPDATED_PROGRAM_RESIDUAL_PERCENT_OVERRIDE,
    offerReduxActionTypes.UPDATED_SELL_RATE_OVERRIDE,
    offerReduxActionTypes.UPDATED_BUY_RATE_OVERRIDE,
    offerReduxActionTypes.UPDATE_RESIDUAL_TAB_DISPLAY_TYPE
]);

const PQ_TRIGGER_ACTIONS: ReadonlySet<string> = new Set([
    offerReduxActionTypes.SELECTED_TERM,
    offerReduxActionTypes.SELECTED_ANNUAL_MILES,
    offerReduxActionTypes.UPDATED_OFFER_TYPE
]);

const MILEAGE_PQ_FIELDS: ReadonlySet<string> = new Set(['standardAllowedMiles', 'annualMiles']);

// DE761542: same reason as programInvalidationMiddleware's exemption. A grid rate or residual edit fans out one
// payment per cash-down row off this single action, so stamping it and then bumping the id for the endpoint
// switch below marked every sibling row stale and discarded its response. The trigger still fires - only the
// stamp is skipped - so the PQ->UPQ switch is unchanged.
const STAMP_EXEMPT_ACTIONS: ReadonlySet<string> = new Set([
    offerReduxActionTypes.UPDATED_SELL_RATE_OVERRIDE,
    offerReduxActionTypes.UPDATED_BASE_RESIDUAL_DOLLAR_OVERRIDE,
    offerReduxActionTypes.UPDATED_BASE_RESIDUAL_PERCENT_OVERRIDE,
    offerReduxActionTypes.UPDATED_PROGRAM_RESIDUAL_DOLLAR_OVERRIDE,
    offerReduxActionTypes.UPDATED_PROGRAM_RESIDUAL_PERCENT_OVERRIDE
]);

export { CALC_REQUEST_ID_META };

const actionTriggersPayment = (action: AnyFSA): boolean => Boolean(action.meta?.middleware?.payment);

const isMileagePqTrigger = (action: AnyFSA): boolean => {
    if (action.type !== APPLY_LEASE_CONTRACT_FIELDS) return false;
    const changedFields = action.meta?.changedFields;
    return Array.isArray(changedFields) && changedFields.some((field: string) => MILEAGE_PQ_FIELDS.has(field));
};

export const middleware: ReduxMiddleware<StateTree> = (store: ReduxStore<StateTree>) => {
    const dispatchEndpointSwitch = (endpoint: CalculationEndpointType, offerType: OfferType): void => {
        const switchAction = updateCurrentCalculationEndpoint(endpoint, offerType, { withPayment: true }) as AnyFSA;
        stampRequestId(store, switchAction);
        store.dispatch(switchAction);
    };

    const handlePqFirstFallback = (state: StateTree, offerType: OfferType): void => {
        if (offerReduxSelectors.getIsUserProgramQuotes(state, offerType)) return;
        if (offerReduxSelectors.getDealerForcedUpq(state)) return;

        const programFound = Boolean(offerReduxSelectors.getSelectedTermByOfferType(state, offerType)?.lenderProgram?.programName);
        if (programFound) return;

        store.dispatch(setProgramFallbackOverrides());
        dispatchEndpointSwitch(CALC_ENDPOINTS.userProgramQuotes, offerType);
    };

    return (next: ReduxNext) => (action: AnyFSA) => {
        const stateBefore = store.getState();

        if (!featureToggleSelectors.areFeatureTogglesAvailable(stateBefore)) {
            next(action);
            return;
        }

        if (featureToggleSelectors.isImprovedAutoSelectEnabled(stateBefore)) {
            next(action);
            return;
        }

        if (!featureToggleSelectors.isLeaseResidualTreatmentEnabled(stateBefore)) {
            next(action);
            return;
        }

        const isUpqTrigger = UPQ_TRIGGER_ACTIONS.has(action.type) || mileageOverrideNeedsUserProgramQuotes(stateBefore, action);
        // `standardAllowedMiles` is in both sets, so the same edit moves the deal in opposite directions by
        // toggle - off it pulls UPQ->PQ, on it pushes PQ->UPQ. Intentional: dropping it from
        // `MILEAGE_PQ_FIELDS` instead would change toggle-off behaviour.
        const isPqTrigger = (PQ_TRIGGER_ACTIONS.has(action.type) || isMileagePqTrigger(action)) && !isUpqTrigger;

        const triggerConcernsLease =
            offerReduxSelectors.getCurrentOfferType(stateBefore) === LEASE ||
            (action.type === offerReduxActionTypes.UPDATED_OFFER_TYPE && action.payload === LEASE);

        if (
            (isUpqTrigger || isPqTrigger) &&
            actionTriggersPayment(action) &&
            triggerConcernsLease &&
            !STAMP_EXEMPT_ACTIONS.has(action.type)
        ) {
            stampRequestId(store, action);
        }

        if (action.type === PAYMENT_RECEIVED) {
            markStalePaymentResponse(store, action);
        }

        next(action);

        if (action.meta?.isStalePaymentResponse) {
            return;
        }

        const state = store.getState();
        const offerType = offerReduxSelectors.getCurrentOfferType(state);

        if (offerType !== LEASE) {
            return;
        }

        const currentEndpoint = offerReduxSelectors.getCalculationEndpointByOfferType(state, offerType);
        // Read through offer-redux's gate, not the raw toggle, so this middleware and the grid can never
        // disagree about whether the redesign is in force for this deal.
        const isRedesignEnabled = offerReduxSelectors.isAspPaymentGridRedesignActive(state, offerType);

        // A grid edit carries its column's engine on the request, so it never needs the deal's engine moved. Both
        // directions were reachable from inside the grid, and either would go out `withPayment: true` and dispatch
        // `setProgramFallbackOverrides()` - the deal screen's per-deal answer to a missing program, which does not
        // compose with the grid's per-column one.
        //
        // Scoped to the action, not to the grid being open, so a deal-screen edit made with the grid open still decides
        // the engine as it always did, and the deal-level PQ-first fallback below still sees every deal-screen response.
        if (isRedesignEnabled && isPaymentGridOriginated(action)) {
            return;
        }

        if (isUpqTrigger) {
            if (overrideKeepsCalculationEndpoint(stateBefore, action)) {
                return;
            }
            if (currentEndpoint !== CALC_ENDPOINTS.userProgramQuotes) {
                store.dispatch(setProgramFallbackOverrides());
                dispatchEndpointSwitch(CALC_ENDPOINTS.userProgramQuotes, offerType);
            }
            return;
        }

        if (isPqTrigger) {
            const isUsedVehicle = offerReduxSelectors.getVehicleDetails(state)?.type === AccelerateVehicleCondition.USED;
            if (
                currentEndpoint !== CALC_ENDPOINTS.programQuotes &&
                !offerReduxSelectors.getDealerForcedUpq(state) &&
                !isUsedVehicle
            ) {
                dispatchEndpointSwitch(CALC_ENDPOINTS.programQuotes, offerType);
            }
            return;
        }

        if (action.type === PAYMENT_RECEIVED && featureToggleSelectors.isLeaseResidualPqFirstEnabled(state)) {
            handlePqFirstFallback(state, offerType);
        }
    };
};
